import axios from "axios";
import { decrypt, encrypt } from "encryption_service/react";
import { tokenService } from "../../../utils/tokenService";
import { performSecureLogout } from "../../../utils/logoutUtility";

// --- GLOBAL API RESPONSE INTERCEPTOR ---
// This interceptor ensures data consistency (data dignity) across the web app.
// Since the API response format recently changed and only some places support it,
// we intercept the response globally here and normalize the new format 
// to match the old format. This ensures old web APIs are not impacted.
axios.interceptors.response.use(
  (response) => {
    const resData = response.data;

    // NOTE: You mentioned "my api response is like this" but the JSON was missing.
    // Here is how you implement the normalization safely:

    if (resData && typeof resData === 'object') {
      // Example 1: If the new API format is wrapped inside `Response.data`
      if (resData.Response && resData.Response.data) {
        // Normalize by flattening the data
        // response.data = resData.Response.data;
      }
      // Example 2: If the new API format is `{ status: 'success', data: {...} }`
      else if (resData.status === 'success' && resData.data) {
        // response.data = resData.data;
      }

      // Add your specific data unwrapping logic here based on the new format!
      // By keeping this conditional, existing working APIs will not be affected.
    }

    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default class Api {
  static getBaseURL() {
    return "";
  }

  static async headers(params: any): Promise<{
    Authorization?: string;
    "Content-Type": string;
    "enc-req"?: number;
    "enc-res"?: number;
  }> {
    let access_token: string | null = null;
    try {
      const token = tokenService.getToken();
      if (token && token !== "null" && token !== "undefined") {
        access_token = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      }
    } catch (e) { }

    let headerEncryption = {
      "enc-req": 0,
      "enc-res": 0,
    };
    if (access_token) {
      let headerData = {
        Authorization: access_token,
        "Content-Type": "application/json",
      };
      if (params && params._parts) {
        headerData = {
          Authorization: access_token,
          "Content-Type": "multipart/form-data",
        };
      }
      headerData = { ...headerData, ...headerEncryption };
      return headerData;
    } else {
      return {
        "Content-Type": "application/json",
        ...headerEncryption,
      };
    }
  }

  static encodeUrlParams(url: any, params: any) {
    let paramString = "";
    if (params && params.length > 0) {
      params.forEach((param: any, index: any) => {
        paramString +=
          index > 0
            ? "&" + Object.keys(param)[0] + "=" + Object.values(param)[0]
            : "?" + Object.keys(param)[0] + "=" + Object.values(param)[0];
      });
    }
    url += paramString;

    return url;
  }

  static async xhr(route: any, params: any, verb: any, isRaw: boolean = false) {
    let url = Api.getBaseURL() + route;

    if (verb === "GET" && params) {
      url = Api.encodeUrlParams(url, params);
      params = null;
    }
    const headers: any = await Api.headers(params);

    // Custom HTTP headers for DAR removed to prevent CORS issues.

    let body: any;
    if (isRaw) {
      body = params;
    } else {
      body = await this.generateBody(params, headers, route);
    }

    let options;
    options = Object.assign(
      { method: verb, headers: headers, url: url },
      params ? { data: body } : null
    );

    return axios(options)
      .then(async (resp: any) => {
        if (headers["enc-res"]) {
          let data = await decrypt(resp.data.data, body.Header);
          let result = JSON.parse(data);
          return { data: result };
        } else {
          return resp.data;
        }
      })
      .catch(async (error: any) => {
        const isTokenAuthenticationFailure = error?.response?.status === 401;

        if (isTokenAuthenticationFailure) {
          performSecureLogout(undefined, "/login");
          // Return a never-resolving promise so no toast/error propagates
          return new Promise(() => { });
        }

        if (headers["enc-res"]) {
          let data = await decrypt(error?.response?.data?.data, body.Header);
          let result = JSON.parse(data);
          error["message"] = result.message;
          throw error;
        } else {
          throw error;
        }
      });
  }

  static async generateRequest(params: any, headers: any, customHeaders: any) {
    if (headers["enc-req"] === 1) {
      return { data: await encrypt(JSON.stringify(params), customHeaders) };
    }
    return { data: params };
  }

  static async createHttpError(statusCode: number, message: string, data: any) {
    const error: any = new Error(message);
    error.statusCode = statusCode;
    error.data = data;
    return error;
  }

  static async generateBody(params: any, headers: any, route: string) {
    // Extract endpoint path - handle both relative and absolute URLs
    let endpoint: string;
    try {
      endpoint = new URL(route).pathname;
    } catch {
      // If route is relative, use it directly
      endpoint = route;
    }

    // Extract action from params if it exists (for Header)
    let action = null;
    let requestParams = params;
    if (params && typeof params === "object" && "action" in params) {
      action = params.action;
      // Remove action from request params so it doesn't go into Request.data
      const { action: _, ...rest } = params;
      requestParams = rest;
    }

    let customHeaders: any = {
      device_id: "WEB",
      device_token: "WEB",
      device_type: "WEB",
      guid:
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      epoch: Date.now(),
      url: endpoint,
      service_request_id: endpoint,
    };

    // Add action to Header if provided
    if (action) {
      customHeaders.action = action;
    }

    let body = {
      Header: customHeaders,
      Request: await this.generateRequest(
        requestParams,
        headers,
        customHeaders
      ),
    };
    return body;
  }

  static post(route: any, params: any, isRaw: boolean = false) {
    return this.xhr(route, params, "POST", isRaw);
  }

  static get(route: any, params: any, isRaw: boolean = false) {
    return this.xhr(route, params, "GET", isRaw);
  }

  static async downloadFile(route: string, params: any, isRaw: boolean = true) {
    let url = Api.getBaseURL() + route;
    const headers: any = await Api.headers(params);
    return axios({
      method: "POST",
      url: url,
      data: params,
      headers: headers,
      responseType: "blob",
    });
  }

  /**
   * Sanitizes and normalizes error messages (Finding 25 Remediation - CWE-209)
   * Masks raw SQL, database errors, stack traces, and internal server paths.
   */
  static getErrorMessage(error: any): string {
    if (!error) return "An unexpected error occurred. Please try again.";

    // Helper to check if a string contains technical database/stack/internal leak
    const containsTechnicalLeak = (str: string): boolean => {
      if (!str || typeof str !== "string") return false;
      const lower = str.toLowerCase();
      return (
        lower.includes("sql") ||
        lower.includes("select ") ||
        lower.includes("insert into") ||
        lower.includes("update ") ||
        lower.includes("delete from") ||
        lower.includes("foreign key") ||
        lower.includes("constraint") ||
        lower.includes("ora-") ||
        lower.includes("sqlstate") ||
        lower.includes("sequelize") ||
        lower.includes("typeorm") ||
        lower.includes("prisma") ||
        lower.includes("pg_") ||
        lower.includes("mysql") ||
        lower.includes("syntaxerror") ||
        lower.includes("typeerror") ||
        lower.includes("referenceerror") ||
        lower.includes("at /") ||
        lower.includes("at (") ||
        lower.includes("node_modules") ||
        lower.includes("econnrefused") ||
        lower.includes("etimedout") ||
        lower.includes("enotfound") ||
        lower.includes("undefined is not a") ||
        lower.includes("cannot read propert")
      );
    };

    if (error && error.response) {
      const resp = error.response;
      const status = resp.status;
      const data = resp.data || {};

      // 1. Status >= 500: Server errors MUST be masked
      if (status >= 500) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[API 500 Internal Error Details]:", data);
        }
        return "Internal server error. Please try again later or contact support.";
      }

      // 2. Extract nested message
      let rawMessage =
        data?.data?.message ||
        data?.message ||
        (Array.isArray(data?.errors) && data.errors.length > 0 ? data.errors[0] : null) ||
        (typeof data?.error === "string" ? data.error : null) ||
        resp.statusText;

      if (rawMessage && typeof rawMessage === "string") {
        if (containsTechnicalLeak(rawMessage)) {
          if (process.env.NODE_ENV === "development") {
            console.warn("[Sanitized Technical Error in UI]:", rawMessage);
          }
          return "Request could not be processed due to a server error.";
        }
        return rawMessage;
      }

      // 3. Fallback based on HTTP status codes
      switch (status) {
        case 400:
          return "Invalid request. Please verify your input and try again.";
        case 401:
          return "Session expired or authentication invalid. Please sign in again.";
        case 403:
          return "You do not have permission to perform this action.";
        case 404:
          return "The requested resource was not found.";
        case 409:
          return "A conflict occurred while processing the request.";
        case 429:
          return "Too many requests. Please wait a few moments and try again.";
        default:
          return `Request failed with status ${status}`;
      }
    } else if (error && error.request) {
      return "Unable to connect to the server. Please check your network connection.";
    } else {
      const rawMsg = String(error?.message || "");
      if (containsTechnicalLeak(rawMsg)) {
        return "An error occurred while processing the request.";
      }
      return rawMsg || "An unexpected error occurred.";
    }
  }
}
